import { useEffect, useState } from "react";
import PageHeader from "../../components/pageHeader";
import MainContentLayout from "../../components/mainContentLayout";
import { API_BASE } from "../../config/apiConfig";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../hooks/useToast";
import LoadingOverlay from "../../components/loadingOverlay";
import { FaUserCircle } from "react-icons/fa";
import { fromFirestoreTimestamp, formatDate } from "../../utils/date";

export default function SubscriptionUpgradePage() {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const token = sessionStorage.getItem("access_token");

  const { showToast, ToastContainer } = useToast();

  const player = location.state?.player; // required – no fallback

  const [duration, setDuration] = useState(1);
  const [paymentDate, setPaymentDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [expiryDate, setExpiryDate] = useState(null);
  const [plan, setPlan] = useState("Pro");

  const [loading, setLoading] = useState(false); // For confirm button + overlay

  // Calculate expiry
  useEffect(() => {
    if (!player || !paymentDate) return;

    let base = fromFirestoreTimestamp(player.plan_expires_at);

    if (!base || isNaN(base.getTime())) {
      base = new Date(paymentDate);
    }

    if (isNaN(base.getTime())) return;

    const today = new Date(paymentDate);

    // If player is still active → extend from expiry
    if (player.plan_expires_at) {
      const expiry = fromFirestoreTimestamp(player.plan_expires_at);
      if (expiry > today) base = expiry;
    }

    const newExpiry = new Date(base);
    newExpiry.setMonth(newExpiry.getMonth() + duration);

    if (!isNaN(newExpiry.getTime())) {
      setExpiryDate(newExpiry);
    }
  }, [duration, paymentDate, player]);

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/api/update_subscription/${userId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          plan,
          duration,
          payment_date: paymentDate,
          new_expiry: expiryDate,
        }),
      });

      const data = await res.json();

      if (data.success) {
        // Construct updated player object for detail page
        const updatedPlayer = {
          ...player,
          plan,
          payment_date: paymentDate,
          plan_expires_at: {
            _seconds: Math.floor(new Date(expiryDate).getTime() / 1000),
          },
        };

        navigate(`/subscription/${userId}`, {
          state: {
            msg: "Subscription updated successfully!",
            player: updatedPlayer,
          },
        });
      } else {
        showToast(data.message || "Update failed", "error");
      }
    } catch (err) {
      showToast("Server error", "error");
    }

    setLoading(false);
  };

  if (!player) return <p className="p-4">Missing player data.</p>;

  return (
    <MainContentLayout>
      <PageHeader title="Upgrade Subscription" backTo={`/subscription/${userId}`} stateToPass={{ player }} />
      <ToastContainer />
      <LoadingOverlay show={loading} text="Updating subscription..." />

      <div className="bg-white shadow rounded-xl p-8 max-w-xl mx-auto w-full">

        {/* Avatar */}
        <div className="flex justify-center">
          {player.photo ? (
            <img
              src={player.photo}
              className="w-24 h-24 rounded-full border object-cover"
            />
          ) : (
            <FaUserCircle className="w-24 h-24 text-gray-400" />
          )}
        </div>

        <p className="text-xl font-semibold text-center">{player.fullName}</p>
        <p className="text-sm text-center text-gray-500">{player.email}</p>

        {/* Payment Date */}
        <div>
          <label className="block text-gray-600 mb-1 mt-4">Payment Date</label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="border px-3 py-2 rounded w-full"
          />
        </div>

        {/* Duration */}
        <div className="mt-4">
          <label className="block text-gray-600 mb-1">Duration (Months)</label>
          <select
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="border px-3 py-2 rounded w-full"
          >
            {[1, 3, 6, 12, 24].map((m) => (
              <option key={m} value={m}>
                {m} Month{m > 1 ? "s" : ""}
              </option>
            ))}
          </select>
        </div>

        {/* Plan */}
        <div className="mt-4">
          <label className="block text-gray-600 mb-1">New Plan</label>
          <select
            value={plan}
            onChange={(e) => setPlan(e.target.value)}
            className="border px-3 py-2 rounded w-full"
          >
            <option value="Pro">Pro</option>
            <option value="Premium">Premium</option>
          </select>
        </div>

        {/* Expiry Preview */}
        <div className="mt-4 mb-4">
          <label className="block text-gray-600 mb-1">New Expiry Date</label>
          <input
            type="text"
            value={expiryDate ? formatDate(expiryDate) : "-"}
            disabled
            className="border px-3 py-2 rounded w-full bg-gray-100"
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full py-2 rounded-md text-white transition 
            ${loading ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}
          `}
        >
          {loading ? "Processing..." : "Confirm Upgrade"}
        </button>
      </div>
    </MainContentLayout>
  );
}
